import { useEffect, useRef, useState } from "react";
import { authApi } from "../../lib/api";

/* Google Identity Services, loaded only if the server says a client id is
   configured. Nothing renders otherwise, so this can ship before the
   credential exists without leaving a dead button on the page.

   Google verifies the address, so an account created this way needs no
   further email confirmation from us. */

const GSI_SRC = "https://accounts.google.com/gsi/client";

function loadGsi() {
  if (window.google?.accounts?.id) return Promise.resolve();
  const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
  if (existing) {
    return new Promise((res, rej) => {
      existing.addEventListener("load", res);
      existing.addEventListener("error", rej);
    });
  }
  return new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = res;
    s.onerror = rej;
    document.head.appendChild(s);
  });
}

export default function GoogleSignIn({ onSuccess, onError, text = "signin_with" }) {
  const holder = useRef(null);
  const [clientId, setClientId] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    authApi.config()
      .then((r) => { if (alive) setClientId(r.data?.google_client_id || null); })
      .catch(() => { if (alive) setClientId(null); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!clientId || !holder.current) return;
    let alive = true;

    loadGsi()
      .then(() => {
        if (!alive || !holder.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (resp) => {
            try {
              const { data } = await authApi.google(resp.credential);
              onSuccess?.(data);
            } catch (e) {
              onError?.(e?.response?.data?.detail || "Google sign-in failed");
            }
          },
        });
        window.google.accounts.id.renderButton(holder.current, {
          theme: "outline",
          size: "large",
          shape: "rectangular",
          text,
          width: 320,
        });
      })
      .catch(() => { if (alive) setFailed(true); });

    return () => { alive = false; };
  }, [clientId, text, onSuccess, onError]);

  if (!clientId || failed) return null;

  return (
    <div className="space-y-4">
      <div ref={holder} className="flex justify-center" data-testid="google-signin" />
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-b2-line" />
        <span className="text-[12px] text-b2-faint">or</span>
        <span className="h-px flex-1 bg-b2-line" />
      </div>
    </div>
  );
}
